/**
 * 区锚点 + 区标题反查纯函数（F12，Q5 / Q6 决策）。
 *
 * **锚点 id 形态迁移**：F03 时期 Section 元素 id = `section-<guid>`，一级
 * NavItem `targetAnchor=#<guid>` 无法匹配（管理员配 NavItem 时不知道 guid）。
 * F12 把每个"区"的锚点 id 统一为 type 字符串（如 `problem` / `video`），
 * 与一级 NavItem 约定 `targetAnchor = '#' + type`（`groupAnchorOf`）对齐，
 * AnchorNav `#problem` 直接命中 problem 区容器 / widget 型 Section 根。
 *
 * **区标题来源**（Q5）：一级 NavItem `displayName` 主源 + Copy Catalog
 * `sectionGroupTitles[type]` 兜底。Guid 形态 / `<guid>#<anchor>` 形态的
 * `targetAnchor` 不参与反查（那是二级 NavItem 指向具体 Section，F13 解析）。
 *
 * **两类 type**：
 *   - 卡片型（`CARD_GROUP_TYPES` = problem / method / innovation）：HomePage
 *     渲染 `<GroupSection>` 区容器（区标题 + 内部 `.scu-grid` 卡片）。
 *   - widget 型（`groupsWithoutTitle` = banner / video / intro / gallery /
 *     material / stats / impact）：Section 自带 `SectionCard` 壳直接作 grid
 *     item，不渲染 HomePage 区标题（`sectionGroupIcons` 含 impact 仅作图标
 *     资源声明，不被 HomePage 区标题消费——impact 自带 widget 标题）。
 *
 * 纯函数 + readonly 入参，无 Vue API 依赖，便于单测。
 */
import type { NavItemView } from '@/api/types';
import { copy } from '@/i18n/zh-CN';

const HASH_PREFIX = '#';

/** 一级 NavItem 约定的 targetAnchor 形态：`'#' + type`（如 `#problem`）。 */
export function groupAnchorOf(type: string): string {
  return `${HASH_PREFIX}${type}`;
}

/** 区锚点元素 id = type 字符串本身（`groupAnchorOf` 去 `#` 前缀）。 */
export function anchorIdOf(type: string): string {
  return type;
}

/**
 * 走 `<GroupSection>` 区容器的卡片型 type（1 卡 = 1 Section，区内多卡聚合）。
 * 其余 type 走 widget 型路径（自带壳，不套 HomePage 区容器）。
 */
export const CARD_GROUP_TYPES: ReadonlySet<string> = Object.freeze(
  new Set(['problem', 'method', 'innovation']),
);

export function isCardGroupType(type: string): boolean {
  return CARD_GROUP_TYPES.has(type);
}

/**
 * 不渲染 HomePage 区标题的 type：Section 自带 `SectionCard` / banner 壳，
 * 标题由组件自身渲染。impact 自带 widget 壳 + DataCard grid，不套区标题
 * （用户确认：避免双层 widget 壳）。
 */
export const groupsWithoutTitle: ReadonlySet<string> = Object.freeze(
  new Set(['banner', 'video', 'bottom-video', 'intro', 'gallery', 'material', 'stats', 'impact']),
);

/**
 * 区标题反查（Q5 五边界 case）：
 *   1. 一级 NavItem `targetAnchor='#<type>'` 命中 → 返回其 `displayName`；
 *   2. 多个一级 NavItem 同 `#<type>` → 取 `sort` 升序首个（同 sort 按 id 兜底）；
 *   3. `enabled=false` 仍参与（区标题不依赖 enabled——disabled NavItem 仅
 *      不进 AnchorNav，但区标题仍显示）；
 *   4. Guid / `<guid>#<anchor>` 形态 `targetAnchor` 不参与（!== `#<type>`）；
 *   5. 无任何匹配 → fallback `sectionGroupTitles[type]` + `console.warn`。
 */
export function resolveGroupTitle(
  type: string,
  navItems: readonly NavItemView[],
): string {
  const hash = groupAnchorOf(type);
  const matches = navItems.filter((item) => item.targetAnchor.trim() === hash);
  if (matches.length > 0) {
    matches.sort((a, b) => a.sort - b.sort || a.id.localeCompare(b.id));
    return matches[0]!.displayName;
  }
  // eslint-disable-next-line no-console
  console.warn(
    `[HomePage] group "${type}" missing nav title source, fallback to copy`,
  );
  return copy.sectionGroupTitles[type as keyof typeof copy.sectionGroupTitles] ?? type;
}
